import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BarChart3,
  Check,
  LockKeyhole,
  Mail,
  MessageCircle,
  PhoneCall,
  Radio,
  Sparkles,
  UsersRound,
} from "lucide-react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { saveToken, toastError } from "../../utils/util";
import { UserContext } from "../../context/UserProvider";

const palette = {
  ink: "#16302B",
  muted: "#60736F",
  primary: "#0F766E",
  primaryDark: "#0A5B55",
  mint: "#DDF7EE",
  mintSoft: "#F2FBF8",
  border: "#DCE9E5",
  surface: "#FFFFFF",
};

const channels = [
  {
    name: "WhatsApp",
    description: "Engage customers where conversations happen.",
    icon: MessageCircle,
    color: "#159A67",
    background: "#E8F8F0",
  },
  {
    name: "Voice",
    description: "Run dependable voice-call campaigns at scale.",
    icon: PhoneCall,
    color: "#6658D3",
    background: "#F0EEFF",
  },
  {
    name: "RCS",
    description: "Send rich, interactive messages that convert.",
    icon: Radio,
    color: "#1677B8",
    background: "#EAF6FD",
  },
];

const ChannelCard = ({ channel }) => {
  const Icon = channel.icon;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 1.75,
        bgcolor: "rgba(255,255,255,0.78)",
        border: "1px solid rgba(255,255,255,0.9)",
        borderRadius: 3,
        boxShadow: "0 8px 30px rgba(31, 86, 73, 0.06)",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: 2.25,
          color: channel.color,
          bgcolor: channel.background,
        }}
      >
        <Icon size={20} strokeWidth={2.2} />
      </Box>
      <Box>
        <Typography sx={{ color: palette.ink, fontWeight: 700, fontSize: 14 }}>
          {channel.name}
        </Typography>
        <Typography
          sx={{
            color: palette.muted,
            fontSize: 12.5,
            lineHeight: 1.45,
            mt: 0.25,
          }}
        >
          {channel.description}
        </Typography>
      </Box>
    </Box>
  );
};

const LoginForm = () => {
  const { refreshUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      const { data } = await axios.post(`${API_ENDPOINT}/user/login`, {
        email: email.trim(),
        password,
        rememberMe,
      });

      saveToken(data.token);
      refreshUser();
      navigate("/home");
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const fieldStyles = {
    "& .MuiOutlinedInput-root": {
      minHeight: 54,
      borderRadius: 2.5,
      bgcolor: "#FBFDFC",
      transition: "background-color 160ms ease, box-shadow 160ms ease",
      "& fieldset": { borderColor: palette.border },
      "&:hover fieldset": { borderColor: "#9BBDB5" },
      "&.Mui-focused": {
        bgcolor: palette.surface,
        boxShadow: "0 0 0 4px rgba(15, 118, 110, 0.09)",
      },
      "&.Mui-focused fieldset": {
        borderColor: palette.primary,
        borderWidth: 1,
      },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: palette.primary },
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: "#F8FBFA",
        color: palette.ink,
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(0, 1.08fr) minmax(420px, 0.92fr)",
        },
      }}
    >
      <Box
        sx={{
          order: { xs: 2, md: 1 },
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          minHeight: { xs: "auto", md: "100vh" },
          p: { xs: 3, sm: 5, md: 7, lg: 9 },
          background:
            "linear-gradient(145deg, #F5FCF9 0%, #E8F7F1 58%, #E0F2EC 100%)",
          borderRight: { md: `1px solid ${palette.border}` },
          "&::before": {
            content: '""',
            position: "absolute",
            width: 440,
            height: 440,
            borderRadius: "50%",
            top: -220,
            right: -160,
            background: "rgba(68, 207, 157, 0.16)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            bottom: -190,
            left: -120,
            background: "rgba(15, 118, 110, 0.09)",
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 40,
                height: 40,
                display: "grid",
                placeItems: "center",
                bgcolor: palette.primary,
                color: "white",
                borderRadius: 2.5,
                boxShadow: "0 8px 20px rgba(15, 118, 110, 0.2)",
              }}
            >
              <Sparkles size={21} />
            </Box>
            <Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.1 }}
              >
                Growth with AI
              </Typography>
              <Typography
                sx={{ color: palette.muted, fontSize: 11.5, mt: 0.35 }}
              >
                Customer engagement platform
              </Typography>
            </Box>
          </Stack>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          style={{ position: "relative", zIndex: 1 }}
        >
          <Box sx={{ maxWidth: 650, py: { xs: 7, md: 5 } }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.8,
                px: 1.5,
                py: 0.75,
                mb: 2.5,
                bgcolor: "rgba(255,255,255,0.7)",
                border: "1px solid rgba(15,118,110,0.13)",
                borderRadius: 99,
                color: palette.primary,
              }}
            >
              <BarChart3 size={15} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
                One platform. Every growth channel.
              </Typography>
            </Box>

            <Typography
              component="h1"
              sx={{
                maxWidth: 620,
                fontSize: { xs: "2.35rem", sm: "3.25rem", lg: "4rem" },
                lineHeight: 1.05,
                letterSpacing: "-0.045em",
                fontWeight: 800,
                color: palette.ink,
              }}
            >
              Turn customer signals into{" "}
              <Box component="span" sx={{ color: palette.primary }}>
                meaningful growth.
              </Box>
            </Typography>

            <Typography
              sx={{
                maxWidth: 570,
                mt: 2.5,
                color: palette.muted,
                fontSize: { xs: 16, sm: 18 },
                lineHeight: 1.7,
              }}
            >
              Explore your audience, discover qualified leads, and launch
              personalized marketing campaigns powered by AI.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 1.5,
                mt: 4,
              }}
            >
              {channels.map((channel) => (
                <ChannelCard key={channel.name} channel={channel} />
              ))}
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 3 }}
              sx={{ mt: 3.5 }}
            >
              {["AI-assisted targeting", "Unified campaign insights"].map(
                (feature) => (
                  <Stack
                    key={feature}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        display: "grid",
                        placeItems: "center",
                        borderRadius: "50%",
                        bgcolor: "#CDEFE3",
                        color: palette.primary,
                      }}
                    >
                      <Check size={13} strokeWidth={3} />
                    </Box>
                    <Typography
                      sx={{
                        color: "#46645E",
                        fontSize: 13.5,
                        fontWeight: 600,
                      }}
                    >
                      {feature}
                    </Typography>
                  </Stack>
                )
              )}
            </Stack>
          </Box>
        </motion.div>

        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            position: "relative",
            zIndex: 1,
            color: "#78908A",
            fontSize: 12,
          }}
        >
          Built for teams that want smarter, more human customer engagement.
        </Typography>
      </Box>

      <Box
        sx={{
          order: { xs: 1, md: 2 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: { xs: "100svh", md: "auto" },
          p: { xs: 3, sm: 6, md: 7 },
          bgcolor: palette.surface,
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: 18 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          style={{ width: "100%", maxWidth: 460 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.1}
            sx={{ display: { xs: "flex", md: "none" }, mb: 5 }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: "grid",
                placeItems: "center",
                bgcolor: palette.primary,
                color: "white",
                borderRadius: 2.25,
              }}
            >
              <Sparkles size={18} />
            </Box>
            <Typography sx={{ color: palette.ink, fontWeight: 800 }}>
              Growth with AI
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "inline-grid",
              placeItems: "center",
              width: 48,
              height: 48,
              borderRadius: 3,
              bgcolor: palette.mint,
              color: palette.primary,
              mb: 3,
            }}
          >
            <UsersRound size={23} />
          </Box>

          <Typography
            component="h2"
            sx={{
              fontSize: { xs: 29, sm: 34 },
              fontWeight: 800,
              letterSpacing: "-0.03em",
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{ color: palette.muted, mt: 0.75, mb: 4, lineHeight: 1.6 }}
          >
            Sign in to continue to your growth workspace.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.25}>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                type="email"
                label="Work email"
                placeholder="name@company.com"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                sx={fieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Mail size={19} color="#708681" />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                required
                fullWidth
                id="password"
                name="password"
                label="Password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                sx={fieldStyles}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockKeyhole size={19} color="#708681" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={() =>
                          setShowPassword((visible) => !visible)
                        }
                        edge="end"
                        sx={{ color: "#708681" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mt: 1.25,
                mb: 3,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    size="small"
                    sx={{
                      color: "#9AAEAA",
                      "&.Mui-checked": { color: palette.primary },
                    }}
                  />
                }
                label="Remember me"
                sx={{
                  mr: 0,
                  "& .MuiFormControlLabel-label": {
                    color: palette.muted,
                    fontSize: 13.5,
                  },
                }}
              />
              <Link
                component={RouterLink}
                to="/reset-password"
                underline="hover"
                sx={{
                  color: palette.primary,
                  fontSize: 13.5,
                  fontWeight: 700,
                }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              variant="contained"
              endIcon={!loading ? <ArrowRight size={18} /> : null}
              sx={{
                minHeight: 52,
                borderRadius: 2.5,
                bgcolor: palette.primary,
                color: "white",
                fontSize: 15.5,
                fontWeight: 700,
                boxShadow: "0 10px 24px rgba(15, 118, 110, 0.2)",
                "&:hover": {
                  bgcolor: palette.primaryDark,
                  boxShadow: "0 12px 28px rgba(15, 118, 110, 0.26)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Sign in to workspace"
              )}
            </Button>

            <Box
              sx={{
                mt: 3,
                p: 2,
                textAlign: "center",
                bgcolor: palette.mintSoft,
                border: `1px solid ${palette.border}`,
                borderRadius: 2.5,
              }}
            >
              <Typography
                component="span"
                sx={{ color: palette.muted, fontSize: 13.5 }}
              >
                New to Growth with AI?{" "}
              </Typography>
              <Link
                component={RouterLink}
                to="/create-user"
                underline="hover"
                sx={{
                  color: palette.primary,
                  fontSize: 13.5,
                  fontWeight: 800,
                }}
              >
                Create an account
              </Link>
            </Box>
          </Box>

          <Typography
            sx={{
              color: "#90A19D",
              fontSize: 11.5,
              textAlign: "center",
              mt: 3,
            }}
          >
            By continuing, you agree to use the platform responsibly and
            securely.
          </Typography>
        </motion.div>
      </Box>
    </Box>
  );
};

export default LoginForm;
